import { useEffect, useState, Fragment } from 'react';
import {
    Typography, Grid, Box, Autocomplete, TextField, Accordion,
    AccordionDetails, Link,
} from '@mui/material';
import MuiAccordionSummary, {
    AccordionSummaryProps,
    accordionSummaryClasses,
} from '@mui/material/AccordionSummary';
import ArrowForwardIosSharpIcon from '@mui/icons-material/ArrowForwardIosSharp';
import { styled } from '@mui/material/styles';
import { Breadcrumb } from 'components';
import faqData from './data/supportData.json';
import trainingData from './data/training.json';

interface FaqItem {
    question: string;
    steps: string[];
    userType?: string;
}

const AccordionSummary = styled((props: AccordionSummaryProps) => (
    <MuiAccordionSummary
        expandIcon={<ArrowForwardIosSharpIcon sx={{ fontSize: '0.9rem' }} />}
        {...props}
    />
))(({ theme }) => ({
    backgroundColor: 'rgba(0, 0, 0, .03)',
    flexDirection: 'row-reverse',
    [`& .${accordionSummaryClasses.expandIconWrapper}.${accordionSummaryClasses.expanded}`]:
    {
        transform: 'rotate(90deg)',
    },
    [`& .${accordionSummaryClasses.content}`]: {
        marginLeft: theme.spacing(2),
    },
    [`& .${accordionSummaryClasses.content}.${accordionSummaryClasses.expanded}`]: {        
        marginLeft: '20px'
    },
    ...(theme.palette.mode === 'dark' && {
        backgroundColor: 'rgba(255, 255, 255, .05)',
    }),
}));

export const Support = () => {
    const [filteredFaqData, setFilteredFaqData] = useState<FaqItem[]>(faqData);
    const [searchText, setSearchText] = useState('');
    const jwt: any = localStorage.getItem('token');
    const labUserCheck: any = jwt ? JSON.parse(atob(jwt.split('.')[1])) : null;
    const [expanded, setExpanded] = useState<string | false>(false);

    useEffect(() => {
        const filteredData = (faqData as FaqItem[])?.filter((faq) => faq?.userType === labUserCheck?.UserType);
        setFilteredFaqData(filteredData);
    }, []);

    const groupedCategories = filteredFaqData.reduce((acc: any, item: any) => {
        if (!acc[item.category]) acc[item.category] = [];
        acc[item.category].push(item);
        return acc;
    }, {});

    const groupedTraining = trainingData
        .filter((item) => item.userType === labUserCheck?.UserType)
        .reduce<Record<string, typeof trainingData>>((acc, item) => {
            if (!acc[item.title]) acc[item.title] = [];
            acc[item.title].push(item);
            return acc;
        }, {});

    const handleAccordionChange = (panel: string) => (_event: React.SyntheticEvent, isExpanded: boolean) => {
        setExpanded(isExpanded ? panel : false);
    };

    let nav: any = [{ name: 'Home', path: '/home/viewQuote' }, { name: 'Support', path: '/home/support' }];

    return (
        <div style={{ padding: '16px 16px', minHeight: "600px" }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', padding: '16px 0', cursor: 'pointer' }}>
                <Breadcrumb navList={nav} />
            </div>
            <Typography variant="h6" gutterBottom align='left' mt={1}>
                Frequently Asked Questions
            </Typography>
            <Grid container spacing={2} mt={4}>
                <Grid item xs={12} sm={12} md={12}>
                    <Autocomplete
                        freeSolo
                        options={filteredFaqData.filter(
                            (faq) =>
                                faq.question.toLowerCase().includes(searchText.toLowerCase()) ||
                                faq.steps.some((step) => step.toLowerCase().includes(searchText.toLowerCase()))
                        )}
                        size='small'
                        sx={{ fontSize: '12px' }}
                        getOptionLabel={(option) => typeof option === 'string' ? option : option.question}
                        inputValue={searchText}
                        onInputChange={(_, value) => {
                            setSearchText(value);
                        }}
                        onChange={(_, value) => {
                            if (value) {
                                const idx = filteredFaqData.findIndex(faq => 
                                    typeof value !== 'string' && faq.question === value.question
                                );
                                if (idx !== -1) {
                                    if (typeof value !== 'string' && value?.question) {
                                        setExpanded(value.question);
                                    }
                                }
                            }
                        }}
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                label="Search for a question"
                                variant="outlined"
                                fullWidth
                                sx={{ mb: 2, fontSize: '12px' }}
                            />
                        )}
                    />
                    <Box
                        sx={{
                            flexGrow: 1,
                            bgcolor: 'background.paper',
                            display: 'flex',
                            height: 'auto'
                        }}
                        role="tabpanel"
                        id="vertical-tab-panel"
                    >
                        {filteredFaqData.length === 0 ? (
                            <Typography variant="h6" align="center">
                                No FAQs available for your user type.
                            </Typography>
                        ) : (
                            <Grid container spacing={2}>
                                {Object.entries(groupedCategories).map(([category, items]: [string, any[]]) => (
                                    <Fragment key={category}>
                                        <Grid item xs={6}>
                                            <Typography variant="h6" gutterBottom align="left" mt={4} mb={2}>
                                                {category}
                                            </Typography>
                                            {items.map((faq, idx) => (
                                                <Accordion
                                                    expanded={expanded === faq.question}
                                                    onChange={handleAccordionChange(faq.question)}
                                                    key={faq.question}
                                                >
                                                    <AccordionSummary
                                                        aria-controls={`panel-content-${idx}`}
                                                        id={`panel-header-${idx}`}
                                                    >
                                                        <Typography variant="subtitle1" sx={{ fontWeight: 500 }}>
                                                            {faq.question}
                                                        </Typography>
                                                    </AccordionSummary>
                                                    <AccordionDetails>
                                                        <ol>
                                                            {faq.steps.map((step: string, stepIdx: number) => (
                                                                <li key={stepIdx}>
                                                                    <Typography variant="body2" color="text.secondary">
                                                                        {step}
                                                                    </Typography>
                                                                </li>
                                                            ))}
                                                        </ol>
                                                    </AccordionDetails>
                                                </Accordion>
                                            ))}
                                        </Grid>
                                    </Fragment>
                                ))}
                            </Grid>
                        )}
                    </Box>
                </Grid>
            </Grid>
            <Grid container spacing={2} mt={4}>
                {Object.entries(groupedTraining).map(([title, items]) => (
                    <Grid item xs={6} key={title}>
                        <Typography variant="h6" gutterBottom align="left" mt={4} mb={2}>
                            {title}
                        </Typography>
                        {items.map((item) => (
                            <Typography key={item.link} variant="body2" paragraph>
                                <Link href={item.link} target="_blank" rel="noopener noreferrer">
                                    {item.linkText}
                                </Link>
                            </Typography>
                        ))}
                    </Grid>
                ))}
            </Grid>
        </div>
    );
}
